'use client';

/**
 * DevelopStageView Component
 *
 * Shows development session status and provides link to full-screen development environment.
 *
 * @see Phase 3: Development Environment UI
 */

import type { Request } from '@cortex/database';
import type { DevSessionStatus } from '@cortex/shared';
import {
  Alert,
  AlertDescription,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Center,
  Heading,
  Inline,
  MetadataField,
  Spinner,
  Stack,
  Text,
} from '@cortex/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import {
  CheckCircle2,
  Circle,
  ExternalLink,
  Loader2,
  Pause,
  Play,
  RotateCcw,
  XCircle,
} from 'lucide-react';
import { useCallback, useState } from 'react';

import { useFetchWithAuth } from '@/hooks/use-api';
import { useSessionByRequest } from '@/hooks/use-session-queries';
import { cn } from '@/lib/utils';

import type { StageViewProps } from './types';

// =============================================================================
// Configuration Objects
// =============================================================================

const statusConfig: Record<
  DevSessionStatus,
  { label: string; icon: typeof Circle; color: string; description: string }
> = {
  provisioning: {
    label: 'Provisioning',
    icon: Loader2,
    color: 'text-info',
    description: 'Setting up your development environment...',
  },
  active: {
    label: 'Active',
    icon: CheckCircle2,
    color: 'text-success',
    description: 'Your development environment is ready',
  },
  suspended: {
    label: 'Suspended',
    icon: Pause,
    color: 'text-warning',
    description: 'Your session has been paused to save resources',
  },
  finalizing: {
    label: 'Finalizing',
    icon: Loader2,
    color: 'text-info',
    description: 'Saving your changes...',
  },
  destroyed: {
    label: 'Destroyed',
    icon: XCircle,
    color: 'text-error',
    description: 'Session has ended',
  },
  failed: {
    label: 'Failed',
    icon: XCircle,
    color: 'text-error',
    description: 'Provisioning failed. You can retry.',
  },
};

// =============================================================================
// Main Component
// =============================================================================

export default function DevelopStageView({
  request,
  onRequestUpdate: _onRequestUpdate,
}: StageViewProps & { onRequestUpdate: (request: Request) => void }) {
  // Note: _onRequestUpdate intentionally unused - reset stays in development stage
  const fetchWithAuth = useFetchWithAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const {
    data: session,
    isPending: isLoading,
    error: queryError,
  } = useSessionByRequest(request.id);

  const [isCreatingSession, setIsCreatingSession] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const displayError = actionError ?? queryError?.message ?? null;

  // Create new session
  const handleCreateSession = useCallback(async () => {
    setIsCreatingSession(true);
    setActionError(null);

    try {
      const response = await fetchWithAuth('/sessions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          requestId: request.id,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create session');
      }

      // Invalidate query so hook refetches the new session
      queryClient.invalidateQueries({
        queryKey: ['dev-session', request.id],
      });

      // Navigate to develop page so user sees provisioning progress
      void navigate({
        to: '/requests/$id/develop',
        params: { id: String(request.request_number) },
      });
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to create session';
      setActionError(errorMessage);
    } finally {
      setIsCreatingSession(false);
    }
  }, [
    request.id,
    request.request_number,
    navigate,
    fetchWithAuth,
    queryClient,
  ]);

  // Reset session (destroy and show "Start Session" again)
  const handleReset = useCallback(async () => {
    if (!session) return;

    setIsResetting(true);
    setActionError(null);

    try {
      const destroyResponse = await fetchWithAuth(
        `/sessions/${session.id}/destroy`,
        { method: 'POST' }
      );

      if (!destroyResponse.ok) {
        throw new Error('Failed to destroy session');
      }

      // Invalidate query so hook refetches (will get 404 -> null)
      queryClient.invalidateQueries({
        queryKey: ['dev-session', request.id],
      });
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to reset session';
      setActionError(errorMessage);
    } finally {
      setIsResetting(false);
    }
  }, [session, fetchWithAuth, queryClient, request.id]);

  // Loading state
  if (isLoading) {
    return (
      <Stack gap="lg">
        <Card className="p-6">
          <Center className="py-12">
            <Spinner size="md" />
          </Center>
        </Card>
      </Stack>
    );
  }

  const config = session
    ? statusConfig[session.status]
    : {
        label: 'Not Started',
        icon: Circle,
        color: 'text-foreground/60',
        description: 'Start a development session to begin coding',
      };
  const StatusIcon = config.icon;

  return (
    <Stack gap="lg">
      {/* Status Card */}
      <Card className="p-6">
        <Inline justify="between" align="center">
          <Inline gap="sm" align="center">
            <StatusIcon
              className={cn(
                'h-6 w-6',
                config.color,
                (session?.status === 'provisioning' ||
                  session?.status === 'finalizing') &&
                  'animate-spin'
              )}
            />
            <Stack gap="xs">
              <Heading level={2} className="text-lg font-semibold">
                Development Environment
              </Heading>
              <Text size="sm" className={cn(config.color)}>
                {config.description}
              </Text>
            </Stack>
          </Inline>

          {/* Action Buttons */}
          <Inline gap="sm" align="center">
            {!session && (
              <Button
                onClick={() => void handleCreateSession()}
                loading={isCreatingSession}
              >
                {!isCreatingSession && <Play className="mr-2 h-4 w-4" />}
                {isCreatingSession ? 'Starting...' : 'Start Development'}
              </Button>
            )}

            {session &&
              (session.status === 'active' ||
                session.status === 'suspended') && (
                <Button
                  onClick={() => {
                    void navigate({
                      to: '/requests/$id/develop',
                      params: { id: String(request.request_number) },
                    });
                  }}
                >
                  <ExternalLink className="mr-2 h-4 w-4" />
                  Open Development Environment
                </Button>
              )}

            {session && session.status === 'provisioning' && (
              <Button
                variant="outline"
                onClick={() => {
                  void navigate({
                    to: '/requests/$id/develop',
                    params: { id: String(request.request_number) },
                  });
                }}
              >
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                View Progress
              </Button>
            )}

            {/* Reset button - always available when session exists */}
            {session && (
              <Button
                variant="ghost"
                onClick={() => void handleReset()}
                loading={isResetting}
                className="text-destructive hover:text-destructive hover:bg-destructive/10"
              >
                {!isResetting && <RotateCcw className="mr-2 h-4 w-4" />}
                {isResetting ? 'Resetting...' : 'Reset'}
              </Button>
            )}
          </Inline>
        </Inline>
      </Card>

      {/* Error Message */}
      {displayError && (
        <Alert variant="destructive">
          <AlertDescription>{displayError}</AlertDescription>
        </Alert>
      )}

      {/* Session Details */}
      {session && (
        <Card className="p-6">
          <Heading level={3} className="mb-4 font-semibold">
            Session Details
          </Heading>

          <Stack gap="sm">
            <MetadataField label="Status" valueSize="sm">
              {config.label}
            </MetadataField>

            {session.machineId && (
              <MetadataField
                label="Machine ID"
                valueSize="xs"
                valueClassName="mt-1 font-mono"
              >
                {session.machineId}
              </MetadataField>
            )}

            {session.flyHostname && (
              <MetadataField
                label="Hostname"
                valueSize="xs"
                valueClassName="mt-1 font-mono"
              >
                {session.flyHostname}
              </MetadataField>
            )}

            <MetadataField label="Created At" valueSize="sm">
              {new Date(session.createdAt).toLocaleString()}
            </MetadataField>

            {session.suspendedAt && (
              <MetadataField label="Suspended At" valueSize="sm">
                {new Date(session.suspendedAt).toLocaleString()}
              </MetadataField>
            )}
          </Stack>
        </Card>
      )}

      {/* Help Text */}
      {!session && (
        <Card className="bg-surface-2">
          <CardHeader>
            <CardTitle>What is the Development Environment?</CardTitle>
          </CardHeader>
          <CardContent>
            <Text color="muted" size="sm">
              A cloud-based development environment with an interactive terminal
              and live preview. Your code changes are saved automatically and
              can be committed when ready.
            </Text>
          </CardContent>
        </Card>
      )}
    </Stack>
  );
}
